import { useEffect, useId, useRef } from 'react';
import { focusReturnTarget } from '@/features/common/focusReturnTarget';
import { FormAlert } from '@/features/common/FormAlert';
import { Icon } from '@/features/common/Icon';
import { useBackdropClose, useNativeDialog } from '@/features/common/useNativeDialog';
import { useFocusAfterBusy } from '@/features/common/useFocusAfterBusy';
import type { InviteDialogProps } from '@/features/publicsite/types/community/InviteDialogProps';
import { publicCommunity } from '@/i18n/dictionaries/publicCommunity';
import { useTranslation } from '@/i18n/useTranslation';

/**
 * The owner's invitation, in MUD's medium modal: the access code in a read-only field — focused on
 * opening, so it can be selected and copied by hand where the clipboard is refused — "Copiază",
 * what to tell the colleagues, and "Generează alt cod".
 */
export function InviteDialog({ invite, fallbackFocus }: InviteDialogProps) {
  const { t } = useTranslation(publicCommunity);
  const titleId = useId();
  const codeId = useId();
  const leadId = useId();
  const element = useNativeDialog(invite.open);
  const codeRef = useRef<HTMLInputElement>(null);
  const copyRef = useRef<HTMLButtonElement>(null);
  const wasOpen = useRef(false);
  const onBackdrop = useBackdropClose(invite.close);

  useEffect(() => {
    if (invite.open) {
      codeRef.current?.focus();
    } else if (wasOpen.current) {
      focusReturnTarget(invite.opener.current, fallbackFocus.current)?.focus();
    }
    wasOpen.current = invite.open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invite.open]);

  useFocusAfterBusy(element, copyRef, invite.busy, invite.open);

  return (
    <dialog
      ref={element}
      className="modal-dialog modal-dialog--md space-invite"
      aria-labelledby={titleId}
      onCancel={(e) => {
        e.preventDefault();
        invite.close();
      }}
      onClose={() => {
        if (invite.open) invite.onClosed();
      }}
      onClick={onBackdrop}
    >
      {invite.open && (
        <div className="modal-dialog__body">
          <button
            type="button"
            className="modal-close"
            aria-label={t('closeDialog')}
            onClick={invite.close}
          >
            <Icon name="cross-large" />
          </button>
          <div className="modal--header">
            <h2 id={titleId} className="modal--header-title">
              {t('inviteTitle')}
            </h2>
          </div>
          <div className="modal-content">
            {invite.refusal && <FormAlert message={invite.refusal} />}
            <div>
              <label className="space-invite__label" htmlFor={codeId}>
                {t('accessCodeLabel')}
              </label>
              <input
                ref={codeRef}
                id={codeId}
                className="text-input space-invite__code"
                readOnly
                aria-describedby={leadId}
                aria-busy={invite.busy}
                placeholder={invite.code === null ? t('accessCodeLoading') : undefined}
                value={invite.code ?? ''}
                onFocus={(e) => e.currentTarget.select()}
              />
            </div>
            <p id={leadId} className="space-invite__lead">
              {t('inviteLead')}
            </p>
            <p className="space-invite__notice" role="status">
              {invite.notice}
            </p>
            <div>
              <button
                type="button"
                className="btn btn--ghost"
                disabled={invite.busy || invite.code === null}
                onClick={() => void invite.renew()}
              >
                {t('renewCode')}
              </button>
            </div>
          </div>
          <div className="modal--footer">
            <div className="modal-buttons">
              <button type="button" className="btn btn--outline" onClick={invite.close}>
                {t('closeInvite')}
              </button>
              <button
                ref={copyRef}
                type="button"
                className="btn btn--primary"
                disabled={invite.code === null}
                onClick={() => void invite.copy()}
              >
                {t('copyCode')}
              </button>
            </div>
          </div>
        </div>
      )}
    </dialog>
  );
}
